import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Alert } from '@mui/material';
import {
	Close as CloseIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLocalization } from 'hooks/useLocalization';
import { useBusiness } from "hooks/useBusiness";
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { ActivityInteractionData } from 'interfaces/ActivityShopUIInterfaces';
import { formatNumberWithSpacesAndRounding, getSingularOrPluralCode } from 'utils/formatUtils';
import ActivityTypeTitleSmall from 'components/activity-display/ActivityTypeTitleSmall';

interface CartItemListProps {
	isInteractive: boolean;
	onEmpty?: () => void;
	activitiesInteractionData: ActivityInteractionData[];
	selectedCurrencyCode: string | null;
}

function CartItemList({ isInteractive, onEmpty, activitiesInteractionData, selectedCurrencyCode }: CartItemListProps) {
	const { getStr } = useLocalization();
	const { activities, activityAddons } = useBusiness();
	const { removeFromCart, getTotalPrice, getTotalPaxQty, getItemsInCartQty, fetchSelectedDeparture, paxArr } = useActivityShopUI();
	const { formatDateToLocal } = useLocalization();
	
	const totalPrice = getTotalPrice(activitiesInteractionData);
	const itemsInCartQty = getItemsInCartQty();
	const handleRemoveActivity = (activityId:number) => {
		removeFromCart(activityId);
		if (itemsInCartQty === 1 && onEmpty) {
			onEmpty();
		}
	}

	const lastCartItemRowPadding = '25px';

	const activitiesInCart = activitiesInteractionData
    .filter((activityInteractionData) =>
        activityInteractionData.isAdded &&
        activityInteractionData.addedAs !== null
    )
    .sort((a, b) => {
        const dateA = new Date(a.dateSelected[a.addedAs as 'private' | 'group']!);
        const dateB = new Date(b.dateSelected[b.addedAs as 'private' | 'group']!);
        return dateA.getTime() - dateB.getTime();
    });
	
	return (
		<>
			{
				itemsInCartQty === 0 ? (
					<Alert severity="error">{getStr('Cart_is_empty')}</Alert>
				) : (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold' }}>{getStr('Activity')}</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">{getStr('Price_pp')} {selectedCurrencyCode}</TableCell>
									{isInteractive && (<TableCell></TableCell>)}
								</TableRow>
							</TableHead>
							<TableBody sx={{ 'td, th': { border: 0 } }}>
								{
									activitiesInCart.map((addedActivityInteractionData) => {
										const activity = activities.find((activity) => activity.id === addedActivityInteractionData.activityId);
										const activityTitle = activity ? activity.title : null;
										const isLastCartItem = activitiesInCart.indexOf(addedActivityInteractionData) === itemsInCartQty - 1;

										if (!activity || !addedActivityInteractionData.addedAs || typeof addedActivityInteractionData.dateSelected[addedActivityInteractionData.addedAs] === 'undefined') return null;

										const addedAddonsInteractionData = addedActivityInteractionData.addons[addedActivityInteractionData.addedAs].filter((addon) => addon.isAdded);
										const activityAddedAddonsQty = addedAddonsInteractionData.length;

										let timeStart;

										if (addedActivityInteractionData.dateSelected.group && addedActivityInteractionData.addedAs === 'group') {
											const departure = fetchSelectedDeparture(addedActivityInteractionData);
											
											if (departure) {
												timeStart = departure.time;
											}
										}

										return (
											<React.Fragment key={activity.id}>
												<TableRow key={activity.id}>
													<TableCell sx={{ paddingBottom: (isLastCartItem && !activityAddedAddonsQty) ? lastCartItemRowPadding : 0 }}>
														<>
															<ActivityTypeTitleSmall activityType={addedActivityInteractionData.addedAs} />
															{
																isInteractive ? (
																	<Link to={`/${getStr('PUBLIC_APP_URL_info')}/${activity.id}/${activity.slug}`} style={{ color: 'black', fontWeight: 'bold' }}
																		className="tt-wp-text-clickable"
																	>
																		{activityTitle}
																	</Link>
																) : (
																	<div style={{ fontWeight: 'bold' }}>{activityTitle}</div>
																)
															}
															<div style={{ fontSize: '0.9em' }}>
																<>
																	{
																		formatDateToLocal(addedActivityInteractionData.dateSelected[addedActivityInteractionData.addedAs] ?? new Date())
																	}
																	{ timeStart ? ` - ${timeStart}` : '' }
																</>
															</div>
														</>
													</TableCell>
													<TableCell align="right" sx={{ paddingBottom: (isLastCartItem && !activityAddedAddonsQty) ? lastCartItemRowPadding : 0 }}>	
														{
															addedActivityInteractionData.totalPrice[addedActivityInteractionData.addedAs] ? (
																formatNumberWithSpacesAndRounding(addedActivityInteractionData.totalPrice[addedActivityInteractionData.addedAs] / getTotalPaxQty(paxArr))
															) : (
																<span style={{ color: 'red' }}>{getStr('Price_unset').toUpperCase()}</span>
															)
														}
													</TableCell>
													{
														isInteractive && (
															<TableCell sx={{ paddingBottom: (isLastCartItem && !activityAddedAddonsQty) ? lastCartItemRowPadding : 0 }}>
																<Tooltip title={getStr('Remove')}>
																	<IconButton 
																		aria-label={getStr('Remove')}
																		color="error"
																		onClick={() => handleRemoveActivity(activity.id)}
																		
																	>
																		<CloseIcon fontSize="inherit" />
																	</IconButton>
																</Tooltip>
															</TableCell>
														)
													}
												</TableRow>
												{
													addedAddonsInteractionData.map((addedAddonInteractionData) => {
														const addon = activityAddons.find((addon) => addon.id === addedAddonInteractionData.addonId);
														const isLastAddon = addedAddonsInteractionData.indexOf(addedAddonInteractionData) === activityAddedAddonsQty - 1;

														if (addon) {
															return (
																<TableRow
																	key={activity.id + '_' + addon.id + '_' + addedActivityInteractionData.addedAs}
																	style={{ fontSize: '0.8em' }}
																>
																	<TableCell sx={{ paddingTop: 0, paddingBottom: (isLastCartItem && isLastAddon) ? lastCartItemRowPadding : 0, fontSize: '0.9em' }}>
																		+ {addon.title}
																	</TableCell>
																	<TableCell align="right" sx={{ paddingTop: 0, paddingBottom: (isLastCartItem && isLastAddon) ? lastCartItemRowPadding : 0, fontSize: '0.9em' }}>
																		{
																			addedAddonInteractionData.totalPrice ? (
																				formatNumberWithSpacesAndRounding(addedAddonInteractionData.totalPrice / getTotalPaxQty(paxArr))
																			) : (
																				<span style={{ color: 'red' }}>{getStr('Price_unset').toUpperCase()}</span>
																			)
																		}
																	</TableCell>
																</TableRow>
															);
														} else {
															return null;
														}
													})
												}
											</React.Fragment>
										)
									})
								}
								<TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">{getStr('Total_pp')}</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">{formatNumberWithSpacesAndRounding(totalPrice / getTotalPaxQty(paxArr))}</TableCell>
									{isInteractive && (<TableCell />)}
								</TableRow>
								<TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">{getStr('Total')} ({getTotalPaxQty(paxArr)} {getStr(getSingularOrPluralCode(getTotalPaxQty(paxArr), 'person', 'people'))})</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">{formatNumberWithSpacesAndRounding(totalPrice)}</TableCell>
									{isInteractive && (<TableCell />)}
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				)
			}
		</>
	);
};

export default CartItemList;